import React, { useRef, useState } from "react";
import { Copy, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DisplayNameField } from "./DisplayNameField";

export const AddWorkerForm = ({ ledger, onSaved }) => {
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [notes, setNotes] = useState("");
  const [email, setEmail] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [pending, setPending] = useState(null);
  const [inviteLink, setInviteLink] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const reset = () => {
    setName(""); setDisplayName(""); setNotes(""); setEmail("");
    setSendInvite(true); setPending(null); setInviteLink(""); setError("");
  };
  const submit = async event => {
    event.preventDefault();
    if (busy.current) return;
    if (!name.trim()) return setError("Enter a worker name.");
    busy.current = true; setSaving(true); setError("");
    let worker = pending;
    try {
      if (!worker) {
        const response = await api.post("/payroll/workers", { name: name.trim(), display_name: displayName, notes: notes.trim(), ledger });
        worker = response.data;
        setPending(worker); onSaved(worker);
      }
      const loginEmail = email.trim().toLowerCase();
      if (loginEmail) {
        const { data } = await api.post(`/mgmt/payroll_workers/${worker.id}/create_user`, { email: loginEmail, send_invite: sendInvite });
        worker = { ...worker, user_id: data.user.id, email: data.user.email, is_worker_manager: data.user.is_worker_manager === true };
        setPending(worker); onSaved(worker);
        if (sendInvite && !data.email_sent) {
          setInviteLink(data.invite_link || "");
          setError("Worker and login saved, but the activation email was not sent. Retry or copy the activation link.");
          return;
        }
        toast.success(sendInvite ? "Worker added. Activation email sent." : "Worker and login saved. No activation email sent.");
      } else {
        toast.success("Worker added without a login.");
      }
      reset();
    } catch (err) {
      const message = formatErr(err.response?.data?.detail) || err.message;
      setError(worker ? `Worker saved. Login setup did not finish: ${message}` : message);
    } finally {
      busy.current = false; setSaving(false);
    }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink); toast.success("Activation link copied."); }
    catch { setError("Could not copy automatically. Select and copy the activation link below."); }
  };
  return <form onSubmit={submit} className="rounded-sm border border-border bg-secondary/20 p-3" data-testid="mgmt-worker-add-form">
    <h4 className="text-xs uppercase text-muted-foreground" data-testid="mgmt-worker-add-heading">{pending ? "Finish worker login" : "Add a worker"}</h4>
    <fieldset disabled={saving || !!pending} className="mt-2 grid min-w-0 grid-cols-1 items-end gap-3 sm:grid-cols-2" data-testid="mgmt-worker-profile-fields">
      <div className="min-w-0 space-y-1"><Label htmlFor="mgmt-worker-name" className="text-xs text-muted-foreground" data-testid="mgmt-worker-name-label">Name</Label><Input id="mgmt-worker-name" required value={name} onChange={event => setName(event.target.value)} placeholder="Name (e.g. Marcus)" data-testid="mgmt-worker-name" /></div>
      <DisplayNameField id="mgmt-worker-display-name" value={displayName} onChange={setDisplayName} />
      <Input aria-label="Worker notes (optional)" value={notes} onChange={event => setNotes(event.target.value)} placeholder="Notes (optional)" className="sm:col-span-2" data-testid="mgmt-worker-notes" />
    </fieldset>
    <div className="mt-3 space-y-3 border-t border-border pt-3">
      <div className="space-y-1"><Label htmlFor="mgmt-worker-login-email" className="text-xs text-muted-foreground" data-testid="mgmt-worker-login-email-label">Email for their login <span className="font-normal">(optional)</span></Label><Input id="mgmt-worker-login-email" type="email" autoComplete="email" value={email} disabled={saving || !!pending?.user_id} onChange={event => setEmail(event.target.value)} placeholder="worker@example.com" data-testid="mgmt-worker-login-email" /></div>
      <div className="flex items-start gap-2"><Checkbox id="mgmt-worker-send-activation" checked={sendInvite} disabled={saving} onCheckedChange={value => setSendInvite(value === true)} data-testid="mgmt-worker-send-activation" /><Label htmlFor="mgmt-worker-send-activation" className="cursor-pointer text-sm font-normal leading-4" data-testid="mgmt-worker-send-activation-label">Email them an activation link</Label></div>
    </div>
    {error && <p role="alert" className="mt-3 text-sm text-destructive [overflow-wrap:anywhere]" data-testid="mgmt-worker-add-error">{error}</p>}
    {inviteLink && <div className="mt-2 flex min-w-0 gap-2"><Input aria-label="Activation link" value={inviteLink} readOnly className="min-w-0" data-testid="mgmt-worker-activation-link" /><Button type="button" variant="outline" size="icon" className="shrink-0" title="Copy activation link" aria-label="Copy activation link" onClick={copyLink} data-testid="mgmt-worker-copy-activation"><Copy className="h-4 w-4" /></Button></div>}
    <div className="mt-3 flex flex-wrap justify-end gap-2">
      {pending && <Button type="button" variant="ghost" disabled={saving} onClick={() => { reset(); toast.success("Worker kept. Login can be managed from the worker row."); }} data-testid="mgmt-worker-keep-saved">Keep saved worker</Button>}
      <Button type="submit" disabled={saving || !name.trim()} className="rounded-sm" data-testid="mgmt-worker-add">{saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}{saving ? "Saving…" : pending ? "Retry login setup" : "Add"}</Button>
    </div>
  </form>;
};